import { usePrevious } from "@react-hookz/web";
import { contains } from "@smartsheet/lodestar-utils";
import isSameDay from "date-fns/isSameDay";
import isSameMonth from "date-fns/isSameMonth";
import React, { forwardRef, useRef, useState, useEffect } from "react";
import { noop } from "../../../../constants/noop";
import { I18nContextProvider, useI18n } from "../../../../i18n/i18n";
import { useCombinedThemeMode } from "../../../../util/theme/useCombinedThemeMode";
import { ClickAwayListener, Flyout } from "../../../flyout";
import { useFocusMode } from "../../../focusMode";
import { injectCatalog } from "../../i18n/DatePickerCatalogs";
import { DatePickerDate, SharedDatePickerDialogProps } from "../../types";
import {
    getVisibleDateFromProps,
    getSinglePickerDayLabel,
    isDayDisabled,
    isValidDate,
    toDatePickerDate,
    getToday,
} from "../../util/calendar";
import { useMemoizedDatePickerDate } from "../hooks/useMemoizedDatePickerDate";
import { DatePickerDayBox } from "../styled/DatePickerDayBox";
import { DatePickerDay } from "./DatePickerDay";
import { DatePickerFrame } from "./DatePickerFrame";

export interface DatePickerDialogProps extends SharedDatePickerDialogProps {
    /**
     * Handler to be called when the "apply" button is clicked.
     *
     * If applyImmediately is true, this will be called whenever a date
     * is clicked.
     */
    onChange: (newDate: DatePickerDate) => void;

    /**
     * The currently selected value of the dialog. If null, the initialPickerDate will be shown.
     *
     * The minDate and maxDate props will still apply to this date. If the value is outside of the
     * min/max range, the dialog will pop up with the closest date (min or max) shown.
     */
    value: DatePickerDate | null;

    /**
     * If true, will omit the clear and apply buttons. This is not allowed in range selection mode.
     * @deprecated this prop doesn't do anything.
     */
    applyImmediately?: boolean;
}

const DatePickerDialogBase = forwardRef<HTMLDivElement, DatePickerDialogProps>(
    (
        {
            "aria-describedby": ariaDescribedby,
            "aria-label": ariaLabel,
            "aria-labelledby": ariaLabelledby,
            // We removed the functionality of this prop since no one is using it. We'll keep it
            // here until a major release since there is one piece of code that declared it but set
            // it to the default value. If we remove this prop, that code will break.
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            applyImmediately = true,
            className,
            clientId,
            dayIsDisabled,
            id,
            initialPickerDate,
            locale,
            maxDate,
            minDate,
            onChange,
            onCloseRequested,
            value,
            onClear,
            ...rest
        },
        ref
    ) => {
        if (process.env.NODE_ENV !== "production") {
            if (value && !isValidDate(value)) {
                console.warn("DatePickerDialog: invalid date.", value);
            }
        }
        const { i18n } = useI18n();
        const { focusMode } = useFocusMode();
        const themeMode = useCombinedThemeMode();

        const dayContainerRef = useRef<HTMLDivElement>(null);

        // Selection is the currently selected date. This is not always the date that is displayed,
        // since the user can move the displayed month around without selection. This date is a
        // reflection of the `value` prop
        const [selection, setSelection] = useState<Date>(() => {
            return getVisibleDateFromProps(value, initialPickerDate || null, minDate || null, maxDate || null);
        });

        // Active date is the date that is currently displayed in the calendar. This is not always
        // the selected date, since the user can move the displayed month around without selection.
        const [activeDate, setActiveDate] = useState(selection);

        const handleDayClick = (date: Date) => {
            setActiveDate(date);
            setSelection(date);
            handleOnChange(date);
        };

        const handleOnChange = (date: Date) => {
            if (!isDayDisabled(date, dayIsDisabled, minDate, maxDate)) {
                onChange(toDatePickerDate(date));
            }
        };

        const handleSpaceOrEnter = () => {
            setSelection(activeDate);
            handleOnChange(activeDate);
        };

        const clickAway = () => {
            onCloseRequested(false);
        };

        const closeDialog = () => {
            onCloseRequested(true);
        };

        // We need this to update the date when a new value is passed to the (already rendered)
        // dialog. usePrevious returns undefined on the first render, so we need to convert that to
        // null so we can compare it to the new value properly (which might be a DatePickerDay or
        // null).
        // We need to memoize everything so we don't fire this effect too often.
        const memoizedPrevValue = useMemoizedDatePickerDate(usePrevious(value) || null);
        const memoizedValue = useMemoizedDatePickerDate(value || null);
        const memoizedMinDate = useMemoizedDatePickerDate(minDate || null);
        const memoizedMaxDate = useMemoizedDatePickerDate(maxDate || null);
        const memiozedInitialPickerDate = useMemoizedDatePickerDate(initialPickerDate || null);
        useEffect(() => {
            if (memoizedPrevValue !== memoizedValue) {
                const newDateFromProps = getVisibleDateFromProps(
                    memoizedValue,
                    memiozedInitialPickerDate,
                    memoizedMinDate,
                    memoizedMaxDate
                );
                setActiveDate(newDateFromProps);
                setSelection(newDateFromProps);
            }
        }, [memiozedInitialPickerDate, memoizedMaxDate, memoizedMinDate, memoizedPrevValue, memoizedValue]);

        return (
            <I18nContextProvider locale={locale}>
                <ClickAwayListener onClickAway={clickAway}>
                    <Flyout
                        {...rest}
                        // No id on this one. We will attach all a11y-relevant
                        // attributes to the DatePickerFrame.
                        id=""
                        // We'll manage this elsewhere.
                        onCloseRequested={noop}
                        closeOnEscape={false}
                        // eslint-disable-next-line jsx-a11y/aria-role
                        role={null}
                    >
                        <DatePickerFrame
                            activeDate={activeDate}
                            activeMonth={activeDate}
                            aria-describedby={ariaDescribedby}
                            aria-label={
                                ariaLabel ||
                                i18n._(
                                    /*i18n*/ {
                                        id: "Date picker open, choose a date",
                                        comment:
                                            "This is text that a screen reader reads out when the user first opens the date picker",
                                    }
                                )
                            }
                            aria-labelledby={ariaLabelledby}
                            className={className}
                            clientId={clientId}
                            dayContainerRef={dayContainerRef}
                            dayIsDisabled={dayIsDisabled}
                            id={id}
                            locale={locale}
                            maxDate={maxDate}
                            minDate={minDate}
                            onCloseRequested={closeDialog}
                            onSpaceOrEnter={handleSpaceOrEnter}
                            ref={ref}
                            requestDateChange={(newDate) => {
                                setActiveDate(newDate);
                            }}
                            onClearClicked={onClear}
                            onTodayClicked={() => {
                                onChange(toDatePickerDate(getToday()));
                            }}
                        >
                            {({ htmlContent, day, key, ...rest }) => {
                                return (
                                    <DatePickerDayBox themeMode={themeMode} key={key}>
                                        <DatePickerDay
                                            {...rest}
                                            // We'll turn off the Clickable's default focus ring so we
                                            // can apply our own that works in Ursa and Lodestar.
                                            omitFocusRing
                                            themeMode={themeMode}
                                            focusMode={focusMode}
                                            aria-label={getSinglePickerDayLabel(i18n, locale, day)}
                                            tabIndex={
                                                // The active selection is focusable, and also the current selection ONLY if it's
                                                // within this month. Otherwise, we could make a date in the first or last row tabbable.
                                                isSameDay(activeDate, day) ||
                                                (isSameDay(selection, day) && isSameMonth(selection, activeDate))
                                                    ? 0
                                                    : -1
                                            }
                                            isSelected={selection !== null && isSameDay(day, selection)}
                                            onClick={() => handleDayClick(day)}
                                            onBlur={(event: React.FocusEvent) => {
                                                // On blur, we'll update this so we get rid of the extra focused day.
                                                // This means we'll only have 1 focusable day (currentSelection) after tabbing.
                                                // If focus leaves the day grid, reset the active date to the selected date ONLY if it's visible.
                                                if (
                                                    !contains(
                                                        dayContainerRef.current,
                                                        event.relatedTarget as Element
                                                    ) &&
                                                    isSameMonth(selection, activeDate)
                                                ) {
                                                    setActiveDate(selection);
                                                }
                                            }}
                                            onFocus={() => {
                                                // If refocusing the selection w/ tab (or arrows, I guess), we'll update this so we get
                                                // rid of the extra focused day. This means we'll only have 1 focusable day after tabbing.
                                                if (isSameDay(day, selection)) {
                                                    setActiveDate(selection);
                                                }
                                            }}
                                        >
                                            {htmlContent}
                                        </DatePickerDay>
                                    </DatePickerDayBox>
                                );
                            }}
                        </DatePickerFrame>
                    </Flyout>
                </ClickAwayListener>
            </I18nContextProvider>
        );
    }
);

DatePickerDialogBase.displayName = "DatePickerDialogBase";

export const DatePickerDialog = forwardRef<HTMLDivElement, DatePickerDialogProps>((props, ref) => {
    injectCatalog();

    return (
        <I18nContextProvider locale={props.locale}>
            <DatePickerDialogBase {...props} ref={ref} />
        </I18nContextProvider>
    );
});

DatePickerDialog.displayName = "DatePickerDialog";
